<script setup>
import { useBlockSafe } from '@/composables/useBlockSafe';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
});
const {
  id,
  backgroundColor,
  spacingTop,
  spacingBottom,
  template,
  title,
  items,
  icons,
  hasTitle,
  hasItems,
  hasIcons,
  hasAnyContent,
} = useBlockSafe(props.data);
const activeIndex = ref(0);

const toggle = (index) => {
  activeIndex.value = activeIndex.value === index ? null : index;
};
</script>

<template>
  <section
    v-if="hasAnyContent"
    :id="id"
    class="section"
    :class="[
      backgroundColor ? `bg-${backgroundColor}` : '',
      spacingTop ? `section--top-${spacingTop}` : 'section--top-xl',
      spacingBottom ? `section--bottom-${spacingBottom}` : 'section--bottom-xl',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div :class="`accordion accordion--${template}`">
        <template v-if="hasTitle">
          <h1
            v-if="template === 'politics'"
            :class="[
              'mb-0 accordion__title h1',
              backgroundColor === 'black' ? 'white' : 'black',
            ]"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="title"
          ></h1>
          <h2
            v-else
            :class="[
              'mb-0 accordion__title',
              backgroundColor === 'black' ? 'white' : 'black',
              template === 'faq' ? 'display' : 'h2',
            ]"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
            v-html="title"
          ></h2>
        </template>
        <ul v-if="hasIcons" class="m-0 p-0 accordion__icons">
          <li
            v-for="(icon, iconIndex) in icons"
            :key="iconIndex"
            class="m-0 accordion__icon"
            data-aos="fade-up"
            :data-aos-delay="iconIndex >= 1 ? 100 + 100 * iconIndex : 100"
          >
            <NuxtImg
              v-if="icon && icon?.src"
              :src="icon.src"
              :alt="icon.alt"
              :height="icon.height"
              :width="icon.width"
              class="accordion__icon-image"
              loading="lazy"
            />
          </li>
        </ul>
        <ul
          class="m-0 p-0 accordion__list"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        >
          <template v-if="hasItems">
            <li
              v-for="(item, index) in items"
              :key="index"
              :class="[
                'm-0 accordion__item',
                { active: activeIndex === index },
                { 'accordion__item--only-text': !item.image },
                `accordion__item--${backgroundColor}`,
              ]"
              @click="toggle(index)"
            >
              <div
                v-if="item.title || item.description"
                class="accordion__item-text"
              >
                <p
                  v-if="item.title"
                  class="mb-0 accordion__item-title h5"
                  :class="[backgroundColor === 'black' ? 'white' : 'black']"
                  v-html="item.title"
                ></p>
                <div
                  v-if="item.description"
                  class="accordion__item-description body-3-regular"
                  :class="[backgroundColor === 'black' ? 'white' : 'black']"
                  v-html="item.description"
                ></div>
              </div>
              <NuxtImg
                v-if="item?.image && item?.image?.src"
                :src="item.image.src"
                :alt="item.image.alt"
                :height="item.image.height"
                :width="item.image.width"
                class="accordion__item-image"
                loading="lazy"
              />
              <Button
                type="button"
                rounded
                variant="outlined"
                class="accordion__item-button button-down js-button-accordion"
              >
                <CIcon
                  class="button-down__icon"
                  :name="
                    backgroundColor === 'black'
                      ? 'direction-white'
                      : 'direction-black'
                  "
                  width="16"
                  height="16"
                />
              </Button>
            </li>
          </template>
          <template v-else>
            <li
              v-for="(item, index) in items"
              :key="index"
              :class="[
                'accordion__item accordion__item--only',
                { active: activeIndex === index },
                { 'accordion__item--only-text': !item.image },
                `accordion__item--${backgroundColor}`,
              ]"
            >
              <div
                v-if="item.title || item.description"
                class="accordion__item-text"
              >
                <p
                  v-if="item.title"
                  class="mb-0 accordion__item-title h5"
                  :class="[backgroundColor === 'black' ? 'white' : 'black']"
                  v-html="item.title"
                ></p>
                <div
                  v-if="item.description"
                  class="accordion__item-description body-3-regular"
                  :class="[backgroundColor === 'black' ? 'white' : 'black']"
                  v-html="item.description"
                ></div>
              </div>
              <NuxtImg
                v-if="item?.image && item?.image?.src"
                :src="item.image.src"
                :alt="item.image.alt"
                :height="item.image.height"
                :width="item.image.width"
                class="accordion__item-image"
                loading="lazy"
              />
            </li>
          </template>
        </ul>
      </div>
    </div>
  </section>
</template>
<style scoped lang="scss">
.accordion {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 56px;
  @include media-breakpoint-up(sm) {
    gap: 80px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 80);
  }
  &__title {
    margin: 0;
    @include media-breakpoint-up(md) {
      max-width: 66%;
    }
  }
  &__icons {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    list-style: none;
    gap: 8px;
    padding: 0;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 8);
    }
  }
  &__icon {
    margin: 0;
    height: 64px;
    width: 64px;
    @include media-breakpoint-up(sm) {
      height: 80px;
      width: 80px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(height, 80);
      @include responsive-spacing(width, 80);
    }
    &-image {
      height: auto;
      width: 100%;
    }
  }
  &__list {
    display: flex;
    flex-direction: column;
    width: 100%;
    list-style: none;
  }
  &__item {
    align-items: start;
    border-top: 1px solid $divider;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-areas: 'text button';
    row-gap: 32px;
    position: relative;
    padding: 24px 0;
    transition: all 0.3s ease;
    &:first-child {
      border-top: unset;
      padding-top: 0;
    }
    &:last-child {
      padding-bottom: 0;
    }
    @include media-breakpoint-up(sm) {
      grid-template-columns: 9fr 3fr;
      padding: 32px 0;
      row-gap: 56px;
    }
    @include media-breakpoint-up(md) {
      column-gap: 16px;
      grid-template-columns: 10fr 2fr;
      grid-template-areas: 'text button';
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(column-gap, 16);
      @include responsive-spacing(row-gap, 56);
      @include responsive-spacing(padding-top, 32);
      @include responsive-spacing(padding-bottom, 32);
    }
    &.active {
      grid-template-areas: 'text button' 'image image';
      padding-bottom: 32px;
      &:last-child {
        padding-bottom: 0;
      }
      @include media-breakpoint-up(sm) {
        padding-bottom: 56px;
      }
      @include media-breakpoint-up(md) {
        grid-template-areas: 'text image button';
        grid-template-columns: 6.08fr 5.05fr 0.87fr;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-bottom, 56);
      }
      #{$parent} {
        &__item {
          &-image {
            display: block;
          }
          &-description {
            display: block;
          }
          &-button {
            transform: rotate(0deg);
          }
        }
      }
    }
    &--only-text {
      &.active {
        grid-template-areas: 'text button';
        @include media-breakpoint-up(md) {
          grid-template-areas: 'text button';
          grid-template-columns: 10fr 2fr;
        }
      }
    }
    &--only {
      grid-template-areas: 'text .';
      @include media-breakpoint-up(sm) {
        grid-template-columns: 9fr 3fr;
      }
      @include media-breakpoint-up(md) {
        grid-template-columns: 10fr 2fr;
        grid-template-areas: 'text .';
      }
      &.active {
        grid-template-areas: 'text .' 'image image';
        @include media-breakpoint-up(md) {
          grid-template-areas: 'text image';
          grid-template-columns: 6fr 6fr;
        }
      }
      #{$parent} {
        &--only-text {
          &.active {
            grid-template-areas: 'text .';
            @include media-breakpoint-up(md) {
              grid-template-areas: 'text .';
              grid-template-columns: 10fr 2fr;
            }
          }
        }
        &__item {
          &-title {
            max-width: unset;
          }
        }
      }
    }
    &-text {
      display: flex;
      flex-direction: column;
      gap: 24px;
      grid-area: text;
      width: 100%;
      &:deep(ul) {
        list-style: disc;
        padding-left: 16px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-left, 16);
        }
        li {
          margin-top: 8px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(margin-top, 8);
          }
        }
      }
      @include media-breakpoint-up(sm) {
        gap: 40px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 40);
      }
    }
    &-image {
      border-radius: 4px;
      display: none;
      grid-area: image;
      width: 100%;
      height: auto;
      @include media-breakpoint-up(md) {
        margin-top: 0px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 4);
      }
    }
    &-title {
      align-items: center;
      display: flex;
      min-height: 48px;
      max-width: calc(100% - 64px);
      @include media-breakpoint-up(sm) {
        max-width: unset;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(min-height, 48);
      }
    }
    &-description {
      display: none;
      @include media-breakpoint-up(md) {
        max-width: 66%;
      }
      &:deep(p:last-child) {
        margin: 0;
      }
    }
    &-button {
      cursor: pointer;
      grid-area: button;
      margin-left: auto;
      position: absolute;
      right: 0;
      transform: rotate(180deg);
      top: 0;
      width: 32px;
      @include media-breakpoint-up(sm) {
        position: static;
        top: unset;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(width, 32);
      }
    }
  }
  &--faq {
    #{$parent} {
      &__list {
        gap: 8px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
        }
      }
      &__title {
        text-align: center;
        max-width: unset;
      }
      &__item {
        border: unset;
        border-radius: 4px;
        padding: 16px;
        @include media-breakpoint-up(sm) {
          padding: 24px;
        }
        @include media-breakpoint-up(md) {
          grid-template-columns: 10.2fr 1.8fr;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-radius, 4);
          @include responsive-spacing(padding, 24);
        }
        &--white {
          background-color: $gray;
        }
        &--gray {
          background-color: $white;
        }
        &--black {
          background-color: $surface-container;
        }
        &-description {
          @include media-breakpoint-up(md) {
            max-width: unset;
          }
        }
      }
    }
  }
  &--politics {
    gap: 52px;
    @include media-breakpoint-up(sm) {
      gap: 80px;
    }
    #{$parent} {
      &__list {
        gap: 18px;
        @include media-breakpoint-up(sm) {
          margin: 0 auto;
          max-width: 83%;
          gap: 26px;
        }
        @include media-breakpoint-up(md) {
          max-width: 66.3%;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 26);
        }
      }
      &__title {
        max-width: unset;
      }
      &__item {
        border: unset;
        padding: 0;
        grid-template-columns: auto 56px;
        @include media-breakpoint-up(xl) {
          grid-template-columns: auto px-to-vw(56);
        }
        &-text {
          gap: 20px;
          @include media-breakpoint-up(sm) {
            gap: 30px;
          }
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(gap, 30);
          }
        }
        &-title {
          max-width: 100%;
          min-height: 32px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(gap, 32);
          }
        }
        &-description {
          @include media-breakpoint-up(md) {
            max-width: unset;
          }
          &:deep(p) {
            margin-bottom: 17px;
            @include media-breakpoint-up(xl) {
              @include responsive-spacing(margin-bottom, 17);
            }
          }
          &:deep(p:last-child) {
            margin-bottom: 0;
          }
        }
        &-button {
          border: unset;
          min-height: 32px;
          min-width: 32px;
          padding: 8px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(min-height, 32);
            @include responsive-spacing(min-width, 32);
            @include responsive-spacing(padding, 8);
          }
        }
        &.active {
          padding-bottom: 16px;
          @include media-breakpoint-up(sm) {
            padding-bottom: 26px;
          }
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(padding-bottom, 26);
          }
        }
      }
    }
  }
}
</style>
